import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { AxiosError } from 'axios';

import { useShippingAddressError } from 'features/my-profile/shipping-address/hooks/useShippingAddressError';
import { useShippingAddressNotification } from 'features/my-profile/shipping-address/hooks/useShippingAddressNotification';
import { useGlobalError } from 'hooks/useGlobalError';
import { userService } from 'services/user/userService';
import {
  ProposedShippingAddressDto,
  ShippingAddressErrorResponse,
  UserShippingAddressDto,
  UserShippingAddressRequestDto,
} from 'services/user/userService.dto';

import { useAuth } from './AuthProvider';

type FetchStatus = 'INIT' | 'IN_PROGRESS' | 'SUCCESS' | 'ERROR';

export type ShippingAddressSaveResult =
  | { type: 'success'; addresses: UserShippingAddressDto[] }
  | { type: 'proposed'; proposedAddress: ProposedShippingAddressDto; request: UserShippingAddressRequestDto }
  | { type: 'error' };

interface ShippingAddressAlertHandlers {
  invalidRequest: () => void;
  addressAlreadyExists: () => void;
  unknownError: () => void;
}

interface ShippingAddressContextType {
  shippingAddresses: UserShippingAddressDto[];
  defaultShippingAddress?: UserShippingAddressDto;
  ensureLoaded: () => void;
  isLoading: boolean;
  isLoaded: boolean;
  isSaving: boolean;
  addShippingAddress: (request: UserShippingAddressRequestDto) => Promise<ShippingAddressSaveResult>;
  updateShippingAddress: (id: number, request: UserShippingAddressRequestDto) => Promise<ShippingAddressSaveResult>;
  deleteShippingAddress: (id: number) => Promise<ShippingAddressSaveResult>;
  setDefaultShippingAddress: (id: number) => Promise<ShippingAddressSaveResult>;
}

const ShippingAddressContext = createContext<ShippingAddressContextType>(null!);
const ShippingAddressAlertsRegisterContext = createContext<(alerts: ShippingAddressAlertHandlers) => void>(() => {});

const noopAlerts: ShippingAddressAlertHandlers = {
  invalidRequest: () => {},
  addressAlreadyExists: () => {},
  unknownError: () => {},
};

/**
 * Binds alert popups into ShippingAddressProvider.
 * Must be rendered under PopupManagerProvider (ShippingAddressProvider sits above it).
 */
const ShippingAddressAlerts = () => {
  const registerAlerts = useContext(ShippingAddressAlertsRegisterContext);
  const { unknownError } = useGlobalError();
  const { invalidRequest, addressAlreadyExists } = useShippingAddressError();

  useLayoutEffect(() => {
    registerAlerts({ invalidRequest, addressAlreadyExists, unknownError });
  }, [registerAlerts, invalidRequest, addressAlreadyExists, unknownError]);

  return null;
};

const ShippingAddressProvider = ({ children }: PropsWithChildren) => {
  const { logged } = useAuth();
  const { shippingAddressAdded, shippingAddressUpdated, shippingAddressDeleted } = useShippingAddressNotification();

  const [fetchStatus, setFetchStatus] = useState<FetchStatus>('INIT');
  const [isSaving, setIsSaving] = useState(false);
  const [shippingAddresses, setShippingAddresses] = useState<UserShippingAddressDto[]>([]);
  const fetchStatusRef = useRef(fetchStatus);
  fetchStatusRef.current = fetchStatus;
  const alertsRef = useRef<ShippingAddressAlertHandlers>(noopAlerts);

  const registerAlerts = useCallback((alerts: ShippingAddressAlertHandlers) => {
    alertsRef.current = alerts;
  }, []);

  const defaultShippingAddress = shippingAddresses.find(shippingAddress => shippingAddress.defaultAddress);
  const isLoading = fetchStatus === 'IN_PROGRESS';
  const isLoaded = fetchStatus === 'SUCCESS' || fetchStatus === 'ERROR';

  const resetStore = useCallback(() => {
    setFetchStatus('INIT');
    setShippingAddresses([]);
    setIsSaving(false);
  }, []);

  useEffect(() => {
    if (!logged) {
      resetStore();
    }
  }, [logged, resetStore]);

  const handleError = (
    error: AxiosError<ShippingAddressErrorResponse>,
    request?: UserShippingAddressRequestDto
  ): ShippingAddressSaveResult => {
    if (error.response?.status === 400) {
      alertsRef.current.invalidRequest();
      return { type: 'error' };
    }
    if (error.response?.status === 401) {
      return { type: 'error' };
    }
    if (error.response?.status === 409) {
      alertsRef.current.addressAlreadyExists();
      return { type: 'error' };
    }
    if (error.response?.status === 422 && error.response?.data.proposedAddress && request) {
      return { type: 'proposed', proposedAddress: error.response.data.proposedAddress, request };
    }
    alertsRef.current.unknownError();
    return { type: 'error' };
  };

  const ensureLoaded = useCallback(() => {
    if (!logged) return;
    if (fetchStatusRef.current === 'IN_PROGRESS' || fetchStatusRef.current === 'SUCCESS') return;

    setFetchStatus('IN_PROGRESS');
    userService
      .fetchShippingAddresses()
      .then(response => {
        setShippingAddresses(response.data);
        setFetchStatus('SUCCESS');
      })
      .catch(error => {
        setFetchStatus('ERROR');
        handleError(error);
      });
  }, [logged]);

  const handleSuccessfulResponse = (data: UserShippingAddressDto[]): ShippingAddressSaveResult => {
    setShippingAddresses(data);
    setFetchStatus('SUCCESS');
    return { type: 'success', addresses: data };
  };

  const addShippingAddress = async (request: UserShippingAddressRequestDto): Promise<ShippingAddressSaveResult> => {
    setIsSaving(true);
    try {
      const response = await userService.addShippingAddress(request);
      const result = handleSuccessfulResponse(response.data);
      shippingAddressAdded();
      return result;
    } catch (error) {
      return handleError(error as AxiosError<ShippingAddressErrorResponse>, request);
    } finally {
      setIsSaving(false);
    }
  };

  const updateShippingAddress = async (
    id: number,
    request: UserShippingAddressRequestDto
  ): Promise<ShippingAddressSaveResult> => {
    setIsSaving(true);
    try {
      const response = await userService.updateShippingAddress(id, request);
      const result = handleSuccessfulResponse(response.data);
      shippingAddressUpdated();
      return result;
    } catch (error) {
      return handleError(error as AxiosError<ShippingAddressErrorResponse>, request);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteShippingAddress = async (id: number): Promise<ShippingAddressSaveResult> => {
    setIsSaving(true);
    try {
      const response = await userService.deleteShippingAddress(id);
      const result = handleSuccessfulResponse(response.data);
      shippingAddressDeleted();
      return result;
    } catch (error) {
      return handleError(error as AxiosError<ShippingAddressErrorResponse>);
    } finally {
      setIsSaving(false);
    }
  };

  const setDefaultShippingAddress = async (id: number): Promise<ShippingAddressSaveResult> => {
    const address = shippingAddresses.find(shippingAddress => shippingAddress.id === id);
    return updateShippingAddress(id, { ...address, defaultAddress: true });
  };

  return (
    <ShippingAddressAlertsRegisterContext.Provider value={registerAlerts}>
      <ShippingAddressContext.Provider
        value={{
          shippingAddresses,
          defaultShippingAddress,
          isLoading,
          isLoaded,
          isSaving,
          ensureLoaded,
          addShippingAddress,
          updateShippingAddress,
          deleteShippingAddress,
          setDefaultShippingAddress,
        }}>
        {children}
      </ShippingAddressContext.Provider>
    </ShippingAddressAlertsRegisterContext.Provider>
  );
};

const useShippingAddress = () => {
  const context = useContext(ShippingAddressContext);
  useEffect(() => {
    context.ensureLoaded();
  }, [context.ensureLoaded]);
  return context;
};

export { ShippingAddressAlerts, ShippingAddressProvider, useShippingAddress };
